import React, { useState, useEffect } from "react";
import { db } from "../../../db/firebase";
import {
  collection,
  query,
  where,
  getDocs,
  updateDoc,
  doc,
  arrayUnion,
  arrayRemove,
  getDoc,
} from "firebase/firestore";
import Skeleton from "react-loading-skeleton";
import "react-loading-skeleton/dist/skeleton.css";
import { useTranslation } from "react-i18next";
import { AnimatedModal, modalPanelClass } from "../../../components/AnimatedModal";
import {
  HiOutlineXMark,
  HiOutlineUserGroup,
  HiOutlineMagnifyingGlass,
} from "react-icons/hi2";
import { getUnavailableUserIdsOnDate } from "../../../utils/timeOff";

const AVATAR_COLORS = [
  "bg-emerald-500",
  "bg-blue-500",
  "bg-violet-500",
  "bg-amber-500",
  "bg-rose-500",
  "bg-cyan-500",
];

const getAvatarColor = (index: number) =>
  AVATAR_COLORS[index % AVATAR_COLORS.length];

interface AddMemberModalProps {
  eventId: string;
  organizationId: string;
  onClose: () => void;
}

const AddMemberModal: React.FC<AddMemberModalProps> = ({
  eventId,
  organizationId,
  onClose,
}) => {
  const { t } = useTranslation();
  const [users, setUsers] = useState<any[]>([]);
  const [members, setMembers] = useState<string[]>([]);
  const [unavailableIds, setUnavailableIds] = useState<Set<string>>(new Set());
  const [eventDate, setEventDate] = useState("");
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [assignError, setAssignError] = useState("");

  const filteredUsers = users.filter((user) => {
    const q = search.trim().toLowerCase();
    if (!q) return true;
    const name = `${user.firstName || ""} ${user.lastName || ""}`.toLowerCase();
    const email = (user.email || "").toLowerCase();
    return name.includes(q) || email.includes(q);
  });

  useEffect(() => {
    const fetchData = async () => {
      const q = query(
        collection(db, "users"),
        where("organizationId", "==", organizationId)
      );
      const snapshot = await getDocs(q);
      const usersData = snapshot.docs.map((d) => ({
        id: d.id,
        ...d.data(),
      }));
      setUsers(usersData);

      const eventDoc = await getDoc(doc(db, "events", eventId));
      const date = eventDoc.exists() ? eventDoc.data().date || "" : "";
      setEventDate(date);
      if (eventDoc.exists()) {
        setMembers(eventDoc.data().members || []);
      }
      if (date) {
        const unavailable = await getUnavailableUserIdsOnDate(
          organizationId,
          date,
          usersData.map((u) => u.id),
        );
        setUnavailableIds(unavailable);
      } else {
        setUnavailableIds(new Set());
      }
      setLoading(false);
    };
    fetchData();
  }, [organizationId, eventId]);

  const handleAddMember = async (userId: string) => {
    setAssignError("");
    if (unavailableIds.has(userId)) {
      setAssignError(
        t("cannotAssignTimeOff") ||
          "This person has approved time off on the event date.",
      );
      return;
    }
    await updateDoc(doc(db, "events", eventId), {
      members: arrayUnion(userId),
    });
    setMembers((prev) => [...prev, userId]);
  };

  const handleRemoveMember = async (userId: string) => {
    await updateDoc(doc(db, "events", eventId), {
      members: arrayRemove(userId),
    });
    setMembers((prev) => prev.filter((id) => id !== userId));
  };

  return (
    <AnimatedModal isOpen onClose={onClose} contentClassName="sm:max-w-xl">
      <div className={modalPanelClass}>
        <div className="sm:hidden flex justify-center pt-2 pb-1 shrink-0">
          <div className="w-10 h-1 rounded-full bg-gray-300 dark:bg-gray-600" />
        </div>
        <div className="flex items-center justify-between px-4 sm:px-6 py-4 sm:py-5 border-b border-gray-100 dark:border-gray-700 bg-gradient-to-r from-gray-50/80 to-white dark:from-gray-800 dark:to-gray-800/80 shrink-0">
          <div className="flex items-center gap-3">
            <div className="flex items-center justify-center w-11 h-11 rounded-xl bg-[#5932EA]/10 dark:bg-[#5932EA]/20 text-[#5932EA] shadow-sm">
              <HiOutlineUserGroup className="w-6 h-6" strokeWidth={2} />
            </div>
            <div>
              <h2 className="text-xl font-semibold text-gray-800 dark:text-gray-100 tracking-tight">
                {t("addRemoveMusicians")}
              </h2>
              <p className="text-sm text-gray-500 dark:text-gray-400 mt-0.5">
                {loading ? "..." : `${users.length}`} {t("members") || "members"}
              </p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="p-2.5 rounded-xl text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
            aria-label={t("close")}
          >
            <HiOutlineXMark className="w-5 h-5" />
          </button>
        </div>
        <div className="px-4 sm:px-6 py-3 border-b border-gray-100 dark:border-gray-700 shrink-0">
          <div className="relative">
            <HiOutlineMagnifyingGlass className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400 pointer-events-none" />
            <input
              type="search"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder={t("searchMembers") || "Search by name or email"}
              autoFocus
              className="w-full pl-10 pr-3 py-2.5 rounded-xl border border-gray-200 dark:border-gray-600 bg-white dark:bg-gray-900 text-gray-800 dark:text-gray-100 placeholder:text-gray-400 focus:outline-none focus:ring-2 focus:ring-[#5932EA]/30 focus:border-[#5932EA]"
            />
          </div>
        </div>
        <div className="flex-1 overflow-y-auto min-h-0">
          <div className="px-6 py-4 border-b border-gray-100 dark:border-gray-700 flex items-center gap-2">
            <div className="w-1 h-5 rounded-full bg-gradient-to-b from-[#5932EA] to-[#7C3AED]" />
            <span className="text-sm font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wide">
              {t("organizationMembers") || "Organization members"}
              {!loading && (
                <span className="ml-1.5 normal-case tracking-normal text-gray-400">
                  ({filteredUsers.length})
                </span>
              )}
            </span>
          </div>
          <div className="p-6">
            {assignError && (
              <p className="mb-3 text-sm text-red-500 dark:text-red-400">{assignError}</p>
            )}
            {eventDate && (
              <p className="mb-3 text-xs text-gray-500 dark:text-gray-400">
                {t("eventDateLabel") || "Event date"}: {eventDate}
              </p>
            )}
            {loading ? (
              <div className="space-y-3">
                {[1, 2, 3, 4, 5, 6].map((i) => (
                  <div key={i} className="flex items-center gap-3">
                    <Skeleton circle width={44} height={44} />
                    <Skeleton width="60%" height={20} />
                  </div>
                ))}
              </div>
            ) : users.length === 0 ? (
              <div className="flex flex-col items-center justify-center py-12 text-gray-500 dark:text-gray-400">
                <HiOutlineUserGroup className="w-14 h-14 mb-3 opacity-40" strokeWidth={1.5} />
                <p className="font-medium">{t("noMembersFound")}</p>
              </div>
            ) : filteredUsers.length === 0 ? (
              <div className="flex flex-col items-center justify-center py-12 text-gray-500 dark:text-gray-400">
                <HiOutlineMagnifyingGlass className="w-14 h-14 mb-3 opacity-40" strokeWidth={1.5} />
                <p className="font-medium">{t("noSearchResults") || "No matches"}</p>
              </div>
            ) : (
              <ul className="space-y-2">
                {filteredUsers.map((user, idx) => {
                  const isMember = members.includes(user.id);
                  const unavailable = unavailableIds.has(user.id);
                  return (
                    <li
                      key={user.id}
                      className={`flex items-center justify-between gap-4 p-4 rounded-xl transition-all border ${
                        isMember
                          ? "bg-emerald-500/10 dark:bg-emerald-500/20 border-emerald-500/30"
                          : unavailable
                            ? "border-amber-200/60 dark:border-amber-800/40 bg-amber-50/40 dark:bg-amber-900/10 opacity-80"
                          : "border-transparent hover:bg-gray-50 dark:hover:bg-gray-700/50 hover:border-gray-100 dark:hover:border-gray-600"
                      }`}
                    >
                      <div className="flex items-center gap-3 min-w-0 flex-1">
                        <div
                          className={`w-11 h-11 rounded-full flex items-center justify-center text-white font-semibold shrink-0 ${getAvatarColor(idx)}`}
                        >
                          {user.firstName?.[0] || user.email?.[0] || "?"}
                        </div>
                        <div className="min-w-0">
                          <span className="block text-gray-800 dark:text-gray-200 truncate font-medium">
                            {user.firstName} {user.lastName}
                          </span>
                          {unavailable ? (
                            <span className="block text-xs text-amber-700 dark:text-amber-300 truncate">
                              {t("unavailableTimeOff") || "Unavailable (time off)"}
                            </span>
                          ) : user.email ? (
                            <span className="block text-xs text-gray-500 dark:text-gray-400 truncate">
                              {user.email}
                            </span>
                          ) : null}
                        </div>
                      </div>
                      <button
                        type="button"
                        disabled={!isMember && unavailable}
                        onClick={() =>
                          isMember ? handleRemoveMember(user.id) : handleAddMember(user.id)
                        }
                        className={`shrink-0 px-4 py-2 rounded-xl text-sm font-medium transition-colors disabled:opacity-50 disabled:cursor-not-allowed ${
                          isMember
                            ? "bg-red-500/10 text-red-600 dark:text-red-400 hover:bg-red-500/20 border border-red-200 dark:border-red-800"
                            : "bg-[#5932EA]/10 text-[#5932EA] hover:bg-[#5932EA]/20 border border-[#5932EA]/30"
                        }`}
                      >
                        {isMember ? t("remove") : t("addMember")}
                      </button>
                    </li>
                  );
                })}
              </ul>
            )}
          </div>
        </div>
      </div>
    </AnimatedModal>
  );
};

export default AddMemberModal;
